import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ManualExecuteCard from "./BinanceManualExecuteCard";
import { Link } from "react-router-dom";
import { FaWallet } from "react-icons/fa";
import "../styles/futuresBot.css";
import DynamicRuleEditor from "../components/DynamicRuleEditor";
import ConditionalRecRuleEditor from "../botSettings/ConditionalRecRuleEditor";
import TpOverrideRuleEditor from "../botSettings/TpOverrideRuleEditor";

const isLocal = window.location.hostname === "localhost";
const API_BASE = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";

const REC_OPTIONS = ["STRONG BUY", "BUY", "STRONG SELL", "SELL"];
const TREND_OPTIONS = ["Extremely Overextended", "Extremely Oversold", "Overextended", "Oversold", "Bull Run",
     "Bear Trend", "Reversing Up", "Reversing Down", "Consolidating", "Unclear", "Bullish", "Bearish"];
const EXPECTED_OPTIONS = ["Neutral", "Bullish",  "Bearish", "Bullish Low", "Bearish Low", "Bullish Starting", 
    "Bearish Starting", "Bearish Medium", "Bullish Medium", "Bearish High", "Bullish High", "Bearish Possible", "Bullish Possible"];

export default function FuturesBotSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState({
    testMode: true,
    leverage: 10,
    qtyUsd: 40,
    entryTolerancePct: 0.6,
    minScore: 0,
    allowedRecommendations: [],
    allowedTrendPhases: [],
    allowedExpected: [],
    tpAdjustPct: 0,
    slAdjustPct: 0,
    minProjectedGainPct: 0,
    maxAcceptedSlPct: 100,
    dynamicQtyRules: { byScore: [], byMarketCap: [] },
    tpOverrideRules: { byScore: [] },
    conditionalRecRules: [],
  });

  const mode = useMemo(() => (config.testMode ? "TEST" : "LIVE"), [config.testMode]);

  async function fetchStatus() {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/binance/bot/status`, { timeout: 15000 });
      setEnabled(Boolean(data?.config?.enabled ?? true));
      if (data?.config) {
        setConfig((prev) => ({
          ...prev,
          ...data.config,
          dynamicQtyRules: data.config.dynamicQtyRules || { byScore: [], byMarketCap: [] },
          tpOverrideRules: data.config.tpOverrideRules || { byScore: [] },
          conditionalRecRules: (data.config.conditionalRecRules || []).map((r) => ({
            ...r,
            allowedRecommendations: r.allowedRecommendations || [],
            pulses: r.pulses || [],
            marketPulseRules: r.marketPulseRules || [],
          })),
        }));
      }
    } catch (e) {
      // if status route not present yet, still render with defaults
      console.warn("status error:", e?.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, []);

  async function pause() {
    try {
      await axios.post(`${API_BASE}/api/binance/bot/pause`, {}, { timeout: 15000 });
      setEnabled(false);
    } catch (e) {
      alert(`Pause failed: ${e.message}`);
    }
  }
  async function resume() {
    try {
      await axios.post(`${API_BASE}/api/binance/bot/resume`, {}, { timeout: 15000 });
      setEnabled(true);
    } catch (e) {
      alert(`Resume failed: ${e.message}`);
    }
  }

  async function save() {
    try {
      setSaving(true);
        const payload = {
          ...config,
          leverage: Number(config.leverage),
          qtyUsd: Number(config.qtyUsd),
          entryTolerancePct: Number(config.entryTolerancePct),
          minScore: Number(config.minScore),
          tpAdjustPct: Number(config.tpAdjustPct),
          slAdjustPct: Number(config.slAdjustPct),
          minProjectedGainPct: Number(config.minProjectedGainPct),
          maxAcceptedSlPct: Number(config.maxAcceptedSlPct),
          dynamicQtyRules: config.dynamicQtyRules || { byScore: [], byMarketCap: [] },
          tpOverrideRules: config.tpOverrideRules || { byScore: [] },
          conditionalRecRules: config.conditionalRecRules || []
        };
      await axios.post(`${API_BASE}/api/binance/bot/update-bot-config`, payload, { timeout: 15000 });
      await fetchStatus();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  // ---- helpers for chip multiselects ----
  const toggleChip = (key, value) => {
    setConfig((c) => {
      const arr = new Set(c[key] || []);
      if (arr.has(value)) arr.delete(value);
      else arr.add(value);
      return { ...c, [key]: Array.from(arr) };
    });
  };
  const selectAll = (key) => setConfig((c) => ({ ...c, [key]: [] }));          // [] => allow all
  const clearAll  = (key, sample) => setConfig((c) => ({ ...c, [key]: ["__NONE__"] })); // blocks all

  return (
    <div className="fb-page">
      <div className="fb-top">
        <div className="fb-title">BINANCE BOT SETTINGS</div>
      </div>

      <div className="fb-card fb-status">
        <div className="fb-status-row">
          <span className={`fb-dot ${enabled ? "on" : "off"}`}></span>
          <span className="fb-status-text">{enabled ? "ON" : "OFF"}</span>
          <div className="fb-spacer" />
          <div style={{ display: "flex", justifyContent: "flex-start"}}>
          <Link to="/crg-wallet" className="btn ghost" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <FaWallet size={16} />
          </Link>
        </div>
          <button className="btn ghost" disabled={!enabled} onClick={pause}>Pause</button>
          <button className="btn primary" disabled={enabled} onClick={resume}>Resume</button>
        </div>
      </div>

      <div className="fb-card">
        <div className="fb-grid">
          <div className="fb-field">
            <label>Mode</label>
            <select
              value={mode}
              onChange={(e) => setConfig((c) => ({ ...c, testMode: e.target.value !== "LIVE" }))}
            >
              <option>LIVE</option>
              <option>TEST</option>
            </select>
          </div>

          <div className="fb-field">
            <label>Leverage (x)</label>
            <input
              type="number"
              min="1"
              value={config.leverage}
              onChange={(e) => setConfig((c) => ({ ...c, leverage: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>USD per trade</label>
            <input
              type="number"
              min="1"
              value={config.qtyUsd}
              onChange={(e) => setConfig((c) => ({ ...c, qtyUsd: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>Entry tolerance (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={config.entryTolerancePct}
              onChange={(e) => setConfig((c) => ({ ...c, entryTolerancePct: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>Minimum score</label>
            <input
              type="number"
              step="0.1"
              value={config.minScore}
              onChange={(e) => setConfig((c) => ({ ...c, minScore: e.target.value }))}
            />
          </div>
        </div>

<div className="fb-grid" style={{ marginTop: 12 }}>
  <div className="fb-field">
    <label>TP adjust (%)</label>
    <input
      type="number"
      step="0.1"
      value={config.tpAdjustPct}
      onChange={(e) => setConfig(c => ({ ...c, tpAdjustPct: e.target.value }))}
    />
    <div className="hint">Positive pushes TP further by percentage points from entry. Negative brings TP closer.</div>
  </div>

  <div className="fb-field">
    <label>SL adjust (%)</label>
    <input
      type="number"
      step="0.1"
      value={config.slAdjustPct}
      onChange={(e) => setConfig(c => ({ ...c, slAdjustPct: e.target.value }))}
    />
    <div className="hint">Positive widens SL by percentage points from entry (e.g. 2% → 3%). Negative tightens it.</div>
  </div>

  <div className="fb-field">
    <label>Minimum projected gain (%)</label>
    <input
      type="number"
      step="0.1"
      min="0"
      value={config.minProjectedGainPct}
      onChange={(e) => setConfig(c => ({ ...c, minProjectedGainPct: e.target.value }))}
    />
    <div className="hint">Skip trades whose entry→TP gain is below this.</div>
  </div>

  <div className="fb-field">
    <label>Maximum SL risk (%)</label>
    <input
      type="number"
      step="0.1"
      min="0"
      value={config.maxAcceptedSlPct}
      onChange={(e) => setConfig(c => ({ ...c, maxAcceptedSlPct: e.target.value }))}
    />
    <div className="hint">Skip trades if |entry–SL|/entry exceeds this.</div>
  </div>
</div>


        <DynamicRuleEditor
          label="Score-based Rules"
          rules={config.dynamicQtyRules?.byScore || []}
          setRules={(newRules) =>
            setConfig(c => ({
              ...c,
              dynamicQtyRules: { ...(c.dynamicQtyRules || {}), byScore: newRules }
            }))
          }
        />
        <TpOverrideRuleEditor
          label="Score-based TP Override Rules"
          rules={config.tpOverrideRules?.byScore || []}
          setRules={(newRules) =>
            setConfig((c) => ({
              ...c,
              tpOverrideRules: {
                ...(c.tpOverrideRules || {}),
                byScore: newRules
              }
            }))
          }
        />
        <DynamicRuleEditor
          label="MarketCap-based Rules"
          rules={config.dynamicQtyRules?.byMarketCap || []}
          setRules={(newRules) =>
            setConfig(c => ({
              ...c,
              dynamicQtyRules: { ...(c.dynamicQtyRules || {}), byMarketCap: newRules }
            }))
          }
        />

        <ConditionalRecRuleEditor
          rules={config.conditionalRecRules || []}
          setRules={(newRules) => setConfig(c => ({ ...c, conditionalRecRules: newRules }))}
        />


        {/* chips */}
        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Recommendations</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedRecommendations")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedRecommendations")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {REC_OPTIONS.map((o) => {
              const active = (config.allowedRecommendations || []).includes(o);
              // [] means "allow all" -> show as neutral chips; we still let user pre-select
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedRecommendations", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedRecommendations?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Trend Phase</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedTrendPhases")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedTrendPhases")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {TREND_OPTIONS.map((o) => {
              const active = (config.allowedTrendPhases || []).includes(o);
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedTrendPhases", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedTrendPhases?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Expected Direction (24h)</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedExpected")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedExpected")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {EXPECTED_OPTIONS.map((o) => {
              const active = (config.allowedExpected || []).includes(o);
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedExpected", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedExpected?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-actions-row">
          <button className="btn ghost" onClick={fetchStatus} disabled={loading || saving}>
            Reset
          </button>
          <div className="fb-spacer" />
          <button className="btn primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
        <ManualExecuteCard apiBase={API_BASE} />
      </div>
    </div>
  );
}
